'use client';

import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { forwardRef, useEffect, useState } from 'react';

import {
  IconButton,
  IconButtonLink,
  type IconButtonLinkProps,
  type IconButtonProps,
} from '@/components/ui/icon-button/icon-button.component';

const GLYPH_SIZE = 'w-6 h-6';
const GLYPH_CLASS = `${GLYPH_SIZE} text-gray-500`;

export interface ModalCloseButtonProps extends Omit<IconButtonProps, 'icon' | 'label' | 'size' | 'iconClassName'> {
  label?: string;
  skipInitialFocus?: boolean;
}

export interface ModalCloseLinkProps extends Omit<IconButtonLinkProps, 'icon' | 'label' | 'size' | 'iconClassName'> {
  label?: string;
}

export const ModalCloseButton = forwardRef<HTMLButtonElement, ModalCloseButtonProps>(
  ({ label = 'Fechar', disabled, skipInitialFocus = false, tabIndex, ...props }, ref) => {
    const [focusable, setFocusable] = useState(!skipInitialFocus);

    useEffect(() => {
      if (focusable) {
        return;
      }

      const frame = requestAnimationFrame(() => setFocusable(true));

      return () => cancelAnimationFrame(frame);
    }, [focusable]);

    return (
      <IconButton
        ref={ref}
        icon={faXmark}
        label={label}
        size="sm"
        disabled={disabled}
        iconClassName={disabled ? GLYPH_SIZE : GLYPH_CLASS}
        tabIndex={focusable ? tabIndex : -1}
        title={undefined}
        {...props}
      />
    );
  },
);

ModalCloseButton.displayName = 'ModalCloseButton';

export function ModalCloseLink({ label = 'Fechar', ...props }: ModalCloseLinkProps) {
  return (
    <IconButtonLink
      icon={faXmark}
      label={label}
      size="sm"
      iconClassName={GLYPH_CLASS}
      title={undefined}
      {...props}
    />
  );
}
